import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SidebarLayout from './SidebarLayout';
import StaffSidebarLayout from '../staff/StaffSidebarLayout';
import './shopStyles.css';

function CustomerImport() {
  const navigate = useNavigate();
  const location = useLocation();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  // Determine which layout to use based on route
  const isStaffPortal =
    location.pathname.includes('/dashboard/professional') ||
    location.pathname.includes('/dashboard/freelancer') ||
    location.pathname.includes('/dashboard/booth-renter');
  const LayoutComponent = isStaffPortal ? StaffSidebarLayout : SidebarLayout;

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      setError('');
      setResults(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      if (!droppedFile.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        return;
      }
      setFile(droppedFile);
      setError('');
      setResults(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const token = localStorage.getItem('ogestyle_token');
      const formData = new FormData();
      formData.append('csvFile', file);

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || ''}/api/customers/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Upload failed');
      }

      setResults(data);
      setFile(null);
    } catch (err) {
      setError(err.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const downloadSampleCSV = () => {
    window.open(`${process.env.REACT_APP_API_BASE_URL || ''}/api/customers/sample-csv`, '_blank');
  };

  return (
    <LayoutComponent>
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '40px 20px' }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              background: 'none',
              border: 'none',
              color: '#6b7280',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 0',
              marginBottom: '16px'
            }}
          >
            ← Back
          </button>
          
          <h1 style={{ margin: '0 0 8px 0', fontSize: '32px', fontWeight: '700' }}>
            Import Customers
          </h1>
          <p style={{ margin: 0, color: '#6b7280', fontSize: '16px' }}>
            Upload a CSV file to bulk import your existing customers
          </p>
        </div>

        {/* Instructions Card */}
        <div style={{
          background: '#eff6ff',
          border: '1px solid #3b82f6',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '32px'
        }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600', color: '#1e40af' }}>
            📋 Instructions
          </h3>
          <ul style={{ margin: 0, paddingLeft: '20px', color: '#1e40af', lineHeight: '1.8' }}>
            <li>Download the sample CSV template below to see the required format</li>
            <li>Fill in your customer data (firstName is required, plus email OR phone)</li>
            <li>Upload your completed CSV file</li>
            <li>Review the import results</li>
          </ul>
          
          <button
            onClick={downloadSampleCSV}
            style={{
              marginTop: '16px',
              padding: '10px 20px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            📥 Download Sample CSV Template
          </button>
        </div>

        {/* Upload Area */}
        <div style={{
          background: 'white',
          border: '2px dashed #d1d5db',
          borderRadius: '12px',
          padding: '48px 24px',
          textAlign: 'center',
          marginBottom: '24px',
          cursor: 'pointer',
          transition: 'all 0.2s'
        }}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => document.getElementById('csvFileInput').click()}
        >
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📁</div>
          <p style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}>
            {file ? file.name : 'Drop your CSV file here or click to browse'}
          </p>
          <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
            Maximum file size: 5MB
          </p>
          <input
            id="csvFileInput"
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </div>

        {/* Upload Button */}
        {file && (
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <button
              onClick={handleUpload}
              disabled={uploading}
              style={{
                padding: '16px 48px',
                background: uploading ? '#cbd5e1' : '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '700',
                cursor: uploading ? 'not-allowed' : 'pointer',
                boxShadow: uploading ? 'none' : '0 4px 12px rgba(16, 185, 129, 0.4)'
              }}
            >
              {uploading ? '⏳ Uploading...' : '✓ Upload and Import'}
            </button>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div style={{
            background: '#fef2f2',
            border: '1px solid #ef4444',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px'
          }}>
            <p style={{ margin: 0, color: '#dc2626', fontWeight: '600' }}>
              ❌ {error}
            </p>
          </div>
        )}

        {/* Results */}
        {results && (
          <div style={{
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '32px',
            marginBottom: '32px'
          }}>
            <h2 style={{ margin: '0 0 24px 0', fontSize: '24px', fontWeight: '700' }}>
              Import Results
            </h2>

            {/* Summary Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '32px' }}>
              <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#6b7280' }}>
                  {results.summary.total}
                </div>
                <div style={{ fontSize: '14px', color: '#6b7280', fontWeight: '600' }}>
                  Total Rows
                </div>
              </div>
              
              <div style={{ background: '#d1fae5', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#065f46' }}>
                  {results.summary.imported}
                </div>
                <div style={{ fontSize: '14px', color: '#065f46', fontWeight: '600' }}>
                  ✓ Imported
                </div>
              </div>
              
              <div style={{ background: '#fef3c7', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#92400e' }}>
                  {results.summary.skipped}
                </div>
                <div style={{ fontSize: '14px', color: '#92400e', fontWeight: '600' }}>
                  ⊘ Skipped
                </div>
              </div>
              
              <div style={{ background: '#fee2e2', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#991b1b' }}>
                  {results.summary.errors}
                </div>
                <div style={{ fontSize: '14px', color: '#991b1b', fontWeight: '600' }}>
                  ❌ Errors
                </div>
              </div>
            </div>

            {/* Error Details */}
            {results.errors && results.errors.length > 0 && (
              <div style={{ marginTop: '32px' }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600', color: '#dc2626' }}>
                  Errors ({results.errors.length})
                </h3>
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Row</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Error</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Data</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.errors.map((error, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '12px', fontSize: '14px' }}>{error.row}</td>
                          <td style={{ padding: '12px', fontSize: '14px', color: '#dc2626' }}>{error.error}</td>
                          <td style={{ padding: '12px', fontSize: '12px', color: '#6b7280' }}>
                            {JSON.stringify(error.data)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Skipped Details */}
            {results.skipped && results.skipped.length > 0 && (
              <div style={{ marginTop: '32px' }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600', color: '#92400e' }}>
                  Skipped ({results.skipped.length})
                </h3>
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Row</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Reason</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Customer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.skipped.map((skip, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '12px', fontSize: '14px' }}>{skip.row}</td>
                          <td style={{ padding: '12px', fontSize: '14px', color: '#92400e' }}>{skip.reason}</td>
                          <td style={{ padding: '12px', fontSize: '14px' }}>
                            {skip.data.firstName} {skip.data.lastName}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Success Message */}
            {results.summary.imported > 0 && (
              <div style={{
                marginTop: '32px',
                background: '#d1fae5',
                border: '1px solid #10b981',
                borderRadius: '8px',
                padding: '16px'
              }}>
                <p style={{ margin: 0, color: '#065f46', fontWeight: '600' }}>
                  ✓ Successfully imported {results.summary.imported} customer{results.summary.imported !== 1 ? 's' : ''}!
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </LayoutComponent>
  );
}

export default CustomerImport;
